'use client';

import type { BoxProps } from '@mui/material';

import { useTranslations } from 'next-intl';

import { alpha } from '@mui/material/styles';
import { Box, Stack, Container, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';

export function ContactHero({ sx, ...other }: BoxProps) {
  const t = useTranslations('Contact.hero');

  const META = [
    { icon: 'solar:map-point-bold-duotone', label: t('location') },
    { icon: 'solar:letter-bold-duotone', label: t('email') },
  ];

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        minHeight: { xs: '52vh', md: '52vh' },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        bgcolor: 'grey.900',
        color: 'common.white',
        ...sx,
      }}
      {...other}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: (theme) => `
            radial-gradient(ellipse 50% 80% at 80% 50%, ${alpha(theme.palette.primary.main, 0.28)} 0%, transparent 65%),
            radial-gradient(ellipse 30% 40% at 10% 20%, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 60%),
            linear-gradient(150deg, #0d1520 0%, #141a21 60%, #1c252e 100%)
          `,
        }}
      />

      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: (theme) => `
            linear-gradient(${alpha(theme.palette.primary.main, 0.07)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.07)} 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 75%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 75%)',
        }}
      />

      <Container
        sx={{ position: 'relative', zIndex: 2, py: { xs: 10, md: 10 }, px: { xs: 3, md: 4 } }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            mb: 2,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: 'primary.light',
          }}
        >
          <Box sx={{ width: 20, height: 2, bgcolor: 'primary.light', borderRadius: 1 }} />
          <Box component="span">{t('overline')}</Box>
        </Stack>

        <Typography
          component="h1"
          sx={{
            fontFamily: 'Barlow, sans-serif',
            fontWeight: 800,
            fontSize: { xs: 'clamp(36px, 10vw, 56px)', md: 'clamp(40px, 5.5vw, 72px)' },
            lineHeight: 1,
            letterSpacing: { xs: '-1px', md: '-1.5px' },
            mb: 2.5,
          }}
        >
          {t('title')}
          <br />
          <Box component="span" sx={{ color: 'primary.light' }}>
            {t('subtitle')}
          </Box>
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.75, sm: 3 }}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 2.5 }}
        >
          {META.map((item) => (
            <Stack key={item.label} direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.25,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2),
                  border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon={item.icon} width={18} sx={{ color: 'primary.light' }} />
              </Box>
              <Typography sx={{ fontSize: 15, color: alpha('#ffffff', 0.55) }}>
                {item.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
